import { useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
  useCollectionBinding,
  useSecondaryColor,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { btnOnLight } from '../utils/buttonStyles';
import { relativeLuminance } from '../utils/luminance';
import { PhotoLightbox } from './shared/PhotoLightbox';
import { ctaLinkProps } from '../utils/scheduleTourModal';

const availabilityCtaDef: SlotGroupDefinition = {
  label: 'See Availability CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'SEE AVAILABILITY' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

interface GalleryImage {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
}

type GalleryTab = 'all' | 'apartments' | 'community';

function resolveSrc(photo: GalleryImage): string {
  return photo.image || photo.original_url || '';
}

function normalizeTitle(title: string | null | undefined): string {
  return (title || '').trim().toLowerCase();
}

function isApartmentPhoto(photo: GalleryImage): boolean {
  const t = normalizeTitle(photo.title);
  return t === 'apartment' || t === 'apartments' || t.includes('apartment');
}

function isCommunityPhoto(photo: GalleryImage): boolean {
  const t = normalizeTitle(photo.title);
  return t === 'community' || t.includes('community') || t.includes('amenit');
}

/**
 * Unified photo gallery matching the 505 Sutton layout: All / Apartments /
 * Community filter tabs above a centered 3-column image grid with lightbox.
 * Pulls every photo_gallery item (apartment + community) into one section.
 */
export function Gallery() {
  const availabilityCta = useSlotGroup('gallery.availability_cta', availabilityCtaDef);
  const secondary = useSecondaryColor();
  // Near-white secondary blends into the page — outline hover/active tabs in black.
  const secondaryNeedsBorder = relativeLuminance(secondary) > 0.85;

  const images = useCollectionBinding<GalleryImage>('gallery.images', {
    collection: 'photo_gallery',
  });

  const [activeTab, setActiveTab] = useState<GalleryTab>('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const usable = useMemo(
    () => images.filter((photo) => Boolean(resolveSrc(photo))),
    [images],
  );

  const filtered = useMemo(() => {
    if (activeTab === 'apartments') return usable.filter(isApartmentPhoto);
    if (activeTab === 'community') return usable.filter(isCommunityPhoto);
    // All: apartments first, then community, then any remaining photos.
    const apartments = usable.filter(isApartmentPhoto);
    const community = usable.filter(
      (photo) => !isApartmentPhoto(photo) && isCommunityPhoto(photo),
    );
    const rest = usable.filter(
      (photo) => !isApartmentPhoto(photo) && !isCommunityPhoto(photo),
    );
    return [...apartments, ...community, ...rest];
  }, [usable, activeTab]);

  // Reset lightbox selection when the visible set changes.
  useEffect(() => {
    setSelectedIndex(null);
  }, [activeTab]);

  return (
    <section className="bg-white">
      <div className="photo-gallery">
        <div className="photo-gallery__intro">
          <RichTextContent
            path="gallery.blurb"
            as="div"
            className="photo-gallery__blurb font-body text-sm sm:text-base text-[var(--vibe-color-text)] max-w-3xl mx-auto leading-relaxed [&_a]:text-primary [&_a]:underline"
          />
          <EditableSlotGroup
            groupPath="gallery.availability_cta"
            definition={availabilityCtaDef}
          >
            <a
              {...ctaLinkProps(availabilityCta.url || '/floor-plans', availabilityCta._target)}
              className={`inline-block text-center mt-6 px-10 py-4 text-sm font-medium tracking-[0.2em] uppercase ${btnOnLight}`}
            >
              {availabilityCta.label || 'SEE AVAILABILITY'}
            </a>
          </EditableSlotGroup>
        </div>

        <div className="photo-gallery__tabs" role="tablist" aria-label="Gallery categories">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'all'}
            className={`photo-gallery__tab${activeTab === 'all' ? ' is-active' : ''}${secondaryNeedsBorder ? ' needs-contrast-border' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <RichTextContent path="gallery.tab_all" as="span" />
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'apartments'}
            className={`photo-gallery__tab${activeTab === 'apartments' ? ' is-active' : ''}${secondaryNeedsBorder ? ' needs-contrast-border' : ''}`}
            onClick={() => setActiveTab('apartments')}
          >
            <RichTextContent path="gallery.tab_apartments" as="span" />
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'community'}
            className={`photo-gallery__tab${activeTab === 'community' ? ' is-active' : ''}${secondaryNeedsBorder ? ' needs-contrast-border' : ''}`}
            onClick={() => setActiveTab('community')}
          >
            <RichTextContent path="gallery.tab_community" as="span" />
          </button>
        </div>

        <EditableCollectionBinding bindingPath="gallery.images" label="Gallery Images">
          {filtered.length === 0 ? (
            <div className="photo-gallery__empty">
              <p className="font-body text-lg text-[var(--vibe-color-text-muted)]">
                Gallery images will be displayed here.
              </p>
            </div>
          ) : (
            <ul className="photo-gallery__grid">
              {filtered.map((photo, index) => {
                const src = resolveSrc(photo);
                const alt = photo.alt_text || photo.title || 'Property photo';
                return (
                  <li key={`${src}-${index}`} className="photo-gallery__item">
                    <button
                      type="button"
                      className="photo-gallery__thumb"
                      onClick={() => setSelectedIndex(index)}
                      aria-label={`View ${alt}`}
                    >
                      <VibesitesImage
                        url={src}
                        alt={alt}
                        sizes="(min-width: 960px) 33vw, (min-width: 768px) 48vw, 100vw"
                        className="photo-gallery__img"
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </EditableCollectionBinding>
      </div>

      <PhotoLightbox
        images={filtered}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <style>{`
        .photo-gallery {
          width: 100%;
          max-width: 1300px;
          margin: 0 auto;
          padding: 2.5rem 1rem 3.5rem;
          text-align: center;
        }

        .photo-gallery__intro {
          margin: 0 auto 2rem;
          max-width: 48rem;
        }

        .photo-gallery__blurb p {
          margin: 0 0 0.75em;
        }

        .photo-gallery__blurb p:last-child {
          margin-bottom: 0;
        }

        .photo-gallery__tabs {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 1.25rem;
        }

        .photo-gallery__tab {
          appearance: none;
          border: 1px solid transparent;
          border-radius: 0;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          line-height: 1;
          padding: 0.95rem 1.85rem;
          cursor: pointer;
          text-decoration: none;
          transition: background-color 0.2s ease, opacity 0.2s ease, border-color 0.2s ease;
        }

        .photo-gallery__tab:hover {
          opacity: 0.92;
        }

        .photo-gallery__tab.is-active {
          background: var(--vibe-color-secondary, #2e3246);
          color: var(--vibe-color-text-on-secondary, #fff);
        }

        .photo-gallery__tab.needs-contrast-border:hover,
        .photo-gallery__tab.needs-contrast-border.is-active {
          border-color: #000;
        }

        .photo-gallery__empty {
          padding: 3rem 1rem;
        }

        .photo-gallery__grid {
          list-style: none;
          margin: 0;
          padding: 0;
          width: 100%;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
        }

        .photo-gallery__item {
          flex: 0 0 33.333%;
          max-width: 33.333%;
          height: 290px;
          max-height: 290px;
          margin: 0;
          padding: 5px;
          position: relative;
          overflow: hidden;
          animation: photo-gallery-fade-in 0.35s ease both;
        }

        .photo-gallery__thumb {
          display: block;
          width: 100%;
          height: 100%;
          padding: 0;
          border: 1px solid #555;
          background: transparent;
          cursor: pointer;
          overflow: hidden;
        }

        .photo-gallery__img {
          display: block;
          width: 100% !important;
          height: 100% !important;
          object-fit: cover;
          transition: transform 0.45s ease;
        }

        .photo-gallery__thumb:hover .photo-gallery__img,
        .photo-gallery__thumb:focus-visible .photo-gallery__img {
          transform: scale(1.06);
        }

        @keyframes photo-gallery-fade-in {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (prefers-reduced-motion: reduce) {
          .photo-gallery__item { animation: none; }
          .photo-gallery__img { transition: none; }
        }

        /* Tablet: 2 columns */
        @media (max-width: 959px) and (min-width: 768px) {
          .photo-gallery__item {
            flex: 0 0 48%;
            max-width: 48%;
            height: 220px;
            max-height: 220px;
            margin: 5px;
            padding: 0;
          }
        }

        /* Mobile: single column */
        @media (max-width: 767px) {
          .photo-gallery {
            padding: 1.75rem 0.75rem 2.5rem;
          }

          .photo-gallery__tabs {
            flex-direction: column;
            align-items: stretch;
            gap: 0.35rem;
          }

          .photo-gallery__tab {
            width: 100%;
            margin: 0;
          }

          .photo-gallery__item {
            flex: 0 0 100%;
            max-width: 100%;
            height: 220px;
            max-height: 220px;
            margin: 5px 0;
            padding: 0;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Photo Gallery',
  tokens: {
    'gallery.blurb': {
      label: 'Gallery Blurb',
      type: 'richtext',
      defaultField: 'property_gallery_blurb',
      defaultValue: '',
    },
    'gallery.tab_all': {
      label: 'All Tab Label',
      type: 'richtext',
      defaultValue: '<p>All</p>',
    },
    'gallery.tab_apartments': {
      label: 'Apartments Tab Label',
      type: 'richtext',
      defaultValue: '<p>Apartments</p>',
    },
    'gallery.tab_community': {
      label: 'Community Tab Label',
      type: 'richtext',
      defaultValue: '<p>Community</p>',
    },
  },
  slotGroups: {
    'gallery.availability_cta': availabilityCtaDef,
  },
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Images',
      collection: 'photo_gallery',
      description:
        'All property photos in one grid. Use photo titles of "Apartment" / "Community" so the Apartments and Community tabs can filter the set. The All tab shows every photo.',
    },
  },
};

(Gallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Gallery;
